
import type { Json } from '@/integrations/supabase/types';
import type { 
  CorporateStructure 
} from '@/types/database/common-types';
import type { AdditionalContact, PayrollSchedule } from '@/types/database/application-types';
import type { HealthPlan } from '@/types/database.types';

/**
 * Converts a JSON array to an array of AdditionalContact objects
 */
export const processAdditionalContacts = (contacts: Json | null): AdditionalContact[] => {
  if (!contacts || !Array.isArray(contacts)) return [];
  
  return contacts.map(contact => {
    if (typeof contact !== 'object' || contact === null) {
      return {
        first_name: '',
        last_name: '',
        title: '',
        phone: '',
        email: ''
      };
    }
    
    const typedContact = contact as Record<string, any>;
    
    return {
      first_name: typedContact.first_name || '',
      last_name: typedContact.last_name || '',
      title: typedContact.title || '',
      phone: typedContact.phone || '',
      email: typedContact.email || ''
    };
  });
};

/**
 * Converts a JSON array of schedules to PayrollSchedule[] type
 */
export const processPayrollSchedules = (schedules: Json[] | null): PayrollSchedule[] => {
  if (!schedules || !Array.isArray(schedules)) return [];
  
  return schedules.map(schedule => {
    if (typeof schedule !== 'object' || schedule === null) {
      return { schedule_type: 'Weekly' };
    }
    
    const typedSchedule = schedule as Record<string, any>;
    
    return {
      schedule_type: typedSchedule.schedule_type || 'Weekly',
      next_check_date: typedSchedule.next_check_date,
      pay_period_start: typedSchedule.pay_period_start,
      pay_period_end: typedSchedule.pay_period_end,
      description: typedSchedule.description
    };
  });
};

/**
 * Process health plans data from JSON to strongly typed HealthPlan[]
 */
export const processHealthPlans = (healthPlans: Json | null): HealthPlan[] => {
  if (!healthPlans) return [];
  
  if (Array.isArray(healthPlans)) {
    return healthPlans.map(plan => {
      if (typeof plan !== 'object' || plan === null) {
        return {
          plan_name: '',
          plan_type: '',
          carrier: '',
          annual_premium: '',
          employee_contribution: '',
          employer_contribution: ''
        };
      }
      
      const typedPlan = plan as Record<string, any>;
      
      return {
        plan_name: typedPlan.plan_name || '',
        plan_type: typedPlan.plan_type || '',
        carrier: typedPlan.carrier || '',
        annual_premium: typedPlan.annual_premium || '',
        employee_contribution: typedPlan.employee_contribution || '',
        employer_contribution: typedPlan.employer_contribution || ''
      };
    });
  }
  
  // If it's a string, try to parse it as JSON
  if (typeof healthPlans === 'string') {
    try {
      const parsed = JSON.parse(healthPlans);
      if (Array.isArray(parsed)) {
        return processHealthPlans(parsed as Json);
      }
    } catch (e) {
      console.warn('Failed to parse health plans:', e);
    }
  }
  
  return [];
};

/**
 * Utility function to ensure string arrays
 * Handles both DB string format and JS array format
 */
export const ensureStringArray = (value: any): string[] => {
  if (!value) return [];
  
  // Handle string values from DB
  if (typeof value === 'string') {
    // Empty string case
    if (value.trim() === '') return [];
    
    // First, try to parse as JSON if it looks like a JSON string
    if (value.trim().startsWith('[') && value.trim().endsWith(']')) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.map(String) : [String(value)];
      } catch (e) {
        // If parsing fails, split by comma
        return value.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    
    // Otherwise, split by commas (common format for storing arrays as strings in DB)
    return value.split(',').map(s => s.trim()).filter(Boolean);
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  
  // Handle nullish or unexpected values
  return [];
};

/**
 * Helper enum for type checking
 */
enum CorporateStructureEnum {
  LLC = 'LLC',
  SCorporation = 'S-Corporation',
  CCorporation = 'C-Corporation',
  Partnership = 'Partnership',
  SoleProprietorship = 'Sole Proprietorship',
  NonProfit = 'Non-Profit',
  Other = 'Other'
}

/**
 * Convert string value to proper enum value for corporate structure
 * Updated to match the database's expected format
 */
export const normalizeCorporateStructure = (value: unknown): CorporateStructure | null => {
  if (!value) return null;
  
  const normalizedValue = String(value).toLowerCase();
  
  switch (normalizedValue) {
    case 'sole_proprietorship':
    case 'sole proprietorship':
      return 'Sole Proprietorship';
    case 'partnership':
      return 'Partnership';
    case 'llc':
      return 'LLC';
    case 'c_corporation':
    case 'c-corporation':
    case 'c corporation':
    case 'c corp':
      return 'C-Corporation';
    case 's_corporation':
    case 's-corporation':
    case 's corporation':
    case 's corp':
      return 'S-Corporation';
    case 'nonprofit':
    case 'non-profit':
    case 'non profit':
      return 'Non-Profit';
    case 'other':
      return 'Other';
    default:
      // Check for direct matches with the correct formats
      if (Object.values(CorporateStructureEnum).includes(value as CorporateStructureEnum)) {
        return value as CorporateStructure;
      }
      return null;
  }
};

/**
 * Helper to convert arrays to string for database storage
 */
export const stringifyArray = (arr: any[] | null | undefined): string | null => {
  if (!arr || !Array.isArray(arr)) return null;
  return JSON.stringify(arr);
};

/**
 * Parse a string or JSON representation back to an array
 */
export const parseArrayField = (value: string | null | undefined): any[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn('Error parsing array field:', e);
    return [];
  }
};

/**
 * Formats a corporate structure value for display
 */
export const formatCorporateStructure = (structure?: string | null): string => {
  if (!structure) return 'Not specified';
  
  // Simply return the structure value directly
  return structure;
};

/**
 * Convert array to JSON string for DB storage
 */
export const arrayToJsonString = (arr: any[] | null | undefined): string => {
  if (!arr || !Array.isArray(arr)) return '[]';
  return JSON.stringify(arr);
};
